import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { Eye } from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig";
import { DataTable } from "components/ui/DataTable";
import { Pill, PILL_COLORS, TruncatingPill } from "components/ui/Pill";
import { TabSwitcher } from "components/ui/TabSwitcher/TabSwitcher";
import { usePatientAppointmentsPaginatedList } from "features/appointments/hooks/usePatientAppointmentsPaginatedList";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { pathGenerators } from "utils/routes";

import s from "./Appointments.module.css";
import { AppointmentDetailsHoverCard } from "./components/AppointmentDetailsHoverCard/AppointmentDetailsHoverCard";
import {
  formatAppointmentStatus,
  formatConsultationTypeLabel,
  formatStaffName,
  getConsultationTypeCategory,
  parseAppointmentStartTime,
} from "./utils/formatAppointment";

const ROWS_OPTIONS = [5, 10, 20, 50, 100];

// Shared pill tokens, so this table matches every other table's badges.
const TYPE_PILL_COLORS = {
  video: PILL_COLORS.statusPending,
  phone: PILL_COLORS.statusWarning,
  inPerson: PILL_COLORS.statusSuccess,
};

const typePillColors = (type) =>
  TYPE_PILL_COLORS[getConsultationTypeCategory(type)];

const statusPillColors = (status) => {
  const normalised = (status ?? "").toUpperCase();
  if (normalised === "CANCELLED") return PILL_COLORS.statusError;
  if (normalised === "COMPLETED") return PILL_COLORS.statusSuccess;

  return PILL_COLORS.statusNoStatus;
};

const isCancelled = (appointment) =>
  (appointment.appointmentStatus ?? "").toUpperCase() === "CANCELLED";

export const Appointments = () => {
  const patientId = useCurrentPatientId();
  const navigate = useNavigate();
  const { data: patient } = usePatientData();
  const [timeframe, setTimeframe] = useState("UPCOMING");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openingAppointmentId, setOpeningAppointmentId] = useState(null);

  const { data, isLoading } = usePatientAppointmentsPaginatedList({
    patientId,
    timeframe,
    page,
    rows: rowsPerPage,
  });

  // The active query already returns its own totalRecords for the
  // visible tab. We fetch a tiny rows=1 page for the OTHER timeframe
  // purely to surface its count on the inactive tab label. React-query
  // caches it so flipping tabs is instant.
  const otherTimeframe = timeframe === "UPCOMING" ? "PREVIOUS" : "UPCOMING";
  const { data: otherCountData } = usePatientAppointmentsPaginatedList({
    patientId,
    timeframe: otherTimeframe,
    page: 1,
    rows: 1,
  });

  const activeTotal = data?.totalRecords;
  const otherTotal = otherCountData?.totalRecords;
  const upcomingCount =
    timeframe === "UPCOMING" ? activeTotal : otherTotal;
  const previousCount =
    timeframe === "PREVIOUS" ? activeTotal : otherTotal;

  const tabOptions = useMemo(
    () => [
      { value: "UPCOMING", label: "Upcoming", count: upcomingCount },
      { value: "PREVIOUS", label: "Past", count: previousCount },
    ],
    [upcomingCount, previousCount]
  );

  const handleTimeframeChange = (next) => {
    if (next === timeframe) return;
    setTimeframe(next);
    setPage(1);
  };

  // Resolves the specific consultation tied to this appointment (rather
  // than just opening whatever the patient's currently active consultation
  // happens to be, which is what /patient/consult shows on its own). If
  // that consultation is the patient's active one we land in the live
  // consult workspace; otherwise it's a past consultation, so we open the
  // read/edit view used elsewhere (ConsultationsTable) for historical
  // consultations.
  const handleViewAppointment = async (appointmentId) => {
    const tenantVal = localStorage.getItem("kinesin-tenant");
    if (!tenantVal) return;

    setOpeningAppointmentId(appointmentId);
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/consultation/getbyappointmentid/${appointmentId}`
      );
      const consultations = response.data ?? [];
      const consultation = consultations
        .slice()
        .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0];

      if (!consultation) {
        toast.error("No consultation has been started for this appointment yet.");
        return;
      }

      if (consultation.id === patient?.activeConsultationId) {
        navigate(pathGenerators.patient.consult(patientId));
      } else {
        navigate(
          pathGenerators.patient.consultationEdit({
            patientId,
            consultationId: consultation.id,
          })
        );
      }
    } catch {
      toast.error("Failed to open this appointment. Please try again.");
    } finally {
      setOpeningAppointmentId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "dateTime",
        header: "Date & Time",
        width: "160px",
        render: (appointment) => {
          const start = parseAppointmentStartTime(appointment.startTime);
          if (!start) return "-";
          const duration = appointment.durationInMins
            ? `${appointment.durationInMins} mins`
            : null;
          return (
            <AppointmentDetailsHoverCard appointment={appointment}>
              <div className={s.dateCell}>
                <span className={s.date}>
                  {start.format("ddd, DD MMM YYYY")}
                </span>
                <span className={s.timeRow}>
                  <span className={s.time}>{start.format("HH:mm")}</span>
                  {!!duration && (
                    <span className={s.duration}>· {duration}</span>
                  )}
                </span>
              </div>
            </AppointmentDetailsHoverCard>
          );
        },
      },
      {
        id: "service",
        header: "Service",
        // 220px: the row was ten pixels past its card, which put a scrollbar under a
        // table that otherwise fits. The service name truncates either way.
        width: "220px",
        render: (appointment) =>
          appointment.practiceService?.name ? (
            <TruncatingPill
              {...PILL_COLORS.method}
              value={appointment.practiceService.name}
            />
          ) : (
            "-"
          ),
      },
      {
        id: "staff",
        header: "Staff",
        width: "130px",
        render: (appointment) =>
          formatStaffName(appointment.assignedToStaffMember) || "-",
      },
      {
        id: "type",
        header: "Type",
        width: "100px",
        render: (appointment) => {
          if (!appointment.consultationType) return "-";

          return (
            <Pill {...typePillColors(appointment.consultationType)}>
              {formatConsultationTypeLabel(appointment.consultationType)}
            </Pill>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        width: "100px",
        render: (appointment) => {
          if (!appointment.appointmentStatus) return "-";

          const statusPill = (
            <Pill {...statusPillColors(appointment.appointmentStatus)}>
              {formatAppointmentStatus(appointment.appointmentStatus)}
            </Pill>
          );

          // The Cancellation Reason column was dropped — it was empty on every
          // row that was not cancelled. The reason now hangs off the status
          // badge, which is the only place it applies.
          if (!isCancelled(appointment) || !appointment.cancellationReason) {
            return statusPill;
          }

          return (
            <Tooltip title={appointment.cancellationReason}>
              <span>{statusPill}</span>
            </Tooltip>
          );
        },
      },
      {
        id: "reason",
        header: "Reason",
        // Only this column flexes — getGridColumns turns
        // { width: "140px", flexGrow: 1 } into "minmax(140px, 1fr)",
        // so the Reason column absorbs whatever space is left in the
        // row without forcing the table wider than its parent card.
        width: "140px",
        flexGrow: 1,
        render: (appointment) => {
          const reason =
            appointment.reasonForVisit || appointment.reasonDescription;
          if (!reason) return "-";

          // Clipped to the column rather than to a character count, so the
          // ellipsis appears exactly when the text does not fit.
          return <TruncatingPill {...PILL_COLORS.method} value={reason} />;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patientId, navigate, patient?.activeConsultationId, openingAppointmentId]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "View",
        icon: Eye,
        primary: true,
        disabled: (appointment) => openingAppointmentId === appointment.id,
        tooltip: (appointment) =>
          openingAppointmentId === appointment.id ? "Opening…" : "View",
        onClick: (appointment) => handleViewAppointment(appointment.id),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openingAppointmentId]
  );

  // The backend orders results by timeframe (UPCOMING soonest-first,
  // PREVIOUS most-recent-first) and slices by page/rows. We pass
  // totalCount so DataTable treats `data.data` as the already-paginated
  // slice (no client-side resort or reslice).
  const items = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  return (
    <div className={s.container}>
      <div className={s.header}>
        <h2 className={s.title}>Appointments</h2>
        <TabSwitcher
          options={tabOptions}
          value={timeframe}
          onChange={handleTimeframeChange}
          size="small"
        />
      </div>
      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        currentPage={page}
        onPageChange={(p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsValue={rowsPerPage}
        onRowsChange={(val) => {
          setRowsPerPage(val);
          setPage(1);
        }}
        rowsOptions={ROWS_OPTIONS}
        totalCount={totalRecords}
        emptyTitle={
          timeframe === "UPCOMING"
            ? "No upcoming appointments"
            : "No past appointments"
        }
        emptyText={
          timeframe === "UPCOMING"
            ? "Schedule an appointment to see it here."
            : "Once this patient has attended an appointment, it will appear here."
        }
        loadingTitle="Loading appointments…"
        loadingText="Please wait while we fetch the patient's appointments."
        getRowId={(row) => row.id}
        rowActions={rowActions}
        rowActionsLabel="Appointment actions"
      />
    </div>
  );
};
